import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloLink,
} from "@apollo/client";
import { LocalState } from "@apollo/client/local-state";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createWSClient } from "./ws";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { constructWithoutUndefined } from "./utils/object";
import { selectedOrganizationIdSubject } from "./business/OrganizationId";
import { notifications } from "@mantine/notifications";
import { GQL_URL, IS_LOCAL } from "./server-select";

const link = ApolloLink.split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return (
      def.kind === "OperationDefinition" && def.operation === "subscription"
    );
  },
  new GraphQLWsLink(createWSClient(GQL_URL)),
  ApolloLink.from([
    setContext(async (_operation, _prevContext) => {
      let token: string | undefined;
      for (let i = 0; i < sessionStorage.length; ++i) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith("oidc.wp")) {
          const value = sessionStorage.getItem(key);
          if (value) {
            token = JSON.parse(value).tokens?.accessToken;
            break;
          }
        }
      }

      return {
        headers: constructWithoutUndefined({
          "organization-id":
            selectedOrganizationIdSubject.getValue() ?? undefined,
          authorization: token ? `Bearer ${token}` : undefined,
        }),
      };
    }),
    new ErrorLink(({ error, forward, operation }) => {
      Sentry.captureException(error);

      const message = (error as any)?.message ?? "An unexpected error occurred";
      if (message !== "No authorization token found") {
        notifications.show({
          title: "Error",
          message: `${message}, please try again later`,
          variant: "error",
          color: "red",
        });
      }

      return forward(operation);
    }),
    new HttpLink({
      uri: `http${IS_LOCAL ? "" : "s"}://` + GQL_URL,
    }),
  ]),
);

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link,

  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },

  /*
  Inserted by Apollo Client 3->4 migration codemod.
  If you are not using the `@client` directive in your application,
  you can safely remove this option.
  */
  localState: new LocalState({}),

  /*
  Inserted by Apollo Client 3->4 migration codemod.
  If you are not using the `@defer` directive in your application,
  you can safely remove this option.
  */
  incrementalHandler: new Defer20220824Handler(),
});

/*
Start: Inserted by Apollo Client 3->4 migration codemod.
Copy the contents of this block into a `.d.ts` file in your project to enable correct response types in your custom links.
If you do not use the `@defer` directive in your application, you can safely remove this block.
*/

import "@apollo/client";
import { Defer20220824Handler } from "@apollo/client/incremental";

declare module "@apollo/client" {
  export interface TypeOverrides extends Defer20220824Handler.TypeOverrides {}
}

/*
End: Inserted by Apollo Client 3->4 migration codemod.
*/
